/**
 * Layout utilities for process recording flow diagrams
 * Uses simple manual horizontal positioning for predictable layout
 */
import type { Node, Edge } from '@xyflow/react';
import { LAYOUT_CONSTANTS, getNodeDimensions } from '@/constants/layoutConstants';
import StartEventNode from '../nodes/StartEventNode';
import StepCardNode from '../nodes/StepCardNode';
import StepWithWhoElseNode from '../nodes/StepWithWhoElseNode';
import SuccessorCardNode from '../nodes/SuccessorCardNode';
import SuccessorsColumnNode from '../nodes/SuccessorsColumnNode';
import WhoElseDoesThisNode from '../nodes/WhoElseDoesThisNode';

/**
 * Node type configuration for React Flow
 * Maps node type strings to their corresponding React components
 */
export const nodeTypes = {
  startEvent: StartEventNode,
  stepCard: StepCardNode,
  whoElseDoesThis: WhoElseDoesThisNode,
  stepWithWhoElse: StepWithWhoElseNode,
  successorCard: SuccessorCardNode,
  successorsColumn: SuccessorsColumnNode,
  group: () => null // Invisible group node
} as const;

/**
 * Calculate node positions using simple horizontal layout
 * Nodes are positioned so their handles align horizontally
 *
 * Layout: [StartEvent] --- [StepWithWhoElse] --- [SuccessorsColumn (with gateway inside)]
 *
 * @param nodes - Array of React Flow nodes to layout
 * @returns Array of layouted nodes with calculated positions
 */
export function getLayoutedElements(nodes: Node[]): Node[] {
  const gap = LAYOUT_CONSTANTS.HORIZONTAL_GAP;

  // Find nodes by type
  const startNode = nodes.find((n) => n.type === 'startEvent');

  // Get dimensions for positioning
  const startDimensions = startNode
    ? getNodeDimensions('startEvent', startNode.data)
    : { width: 0, height: 0 };
  const stepDimensions = getNodeDimensions('stepWithWhoElse');

  // Calculate X positions left-to-right
  let currentX = 0;

  const startX = currentX;
  currentX += startDimensions.width + gap;

  const stepX = currentX;
  currentX += stepDimensions.width + gap;

  const successorsX = currentX;

  // Position calculations
  const previewY = LAYOUT_CONSTANTS.PREVIEW_Y;

  // Initial Y for successors column - will be adjusted by ProcessDiagram
  // after measuring actual node height for precise alignment
  const successorsColumnNode = nodes.find((n) => n.type === 'successorsColumn');
  const successorCount =
    (successorsColumnNode?.data as { successors?: unknown[] })?.successors?.length || 1;
  const isDecision = successorCount > 1;

  // Approximate initial position (ProcessDiagram will correct this after measurement)
  const successorsY = isDecision ? 200 : 450;

  const layoutedNodes = nodes.map((node) => {
    let x = 0;
    let y = 0;

    switch (node.type) {
      case 'startEvent':
        x = startX;
        y = previewY;
        break;
      case 'stepWithWhoElse':
        x = stepX;
        y = 0; // Anchor
        break;
      case 'successorsColumn':
        x = successorsX;
        y = successorsY;
        break;
      default:
        x = node.position.x;
        y = node.position.y;
    }

    return {
      ...node,
      position: { x, y }
    };
  });

  return layoutedNodes;
}

/**
 * Apply consistent styling to edges for the process flow diagram
 *
 * @param edges - Array of React Flow edges
 * @returns Array of edges with applied styling
 */
export function styleEdges(edges: Edge[]): Edge[] {
  return edges.map((edge) => ({
    ...edge,
    style: {
      stroke: '#d1d5db',
      strokeWidth: 2
    },
    ...(edge.label
      ? {
          labelStyle: {
            fontSize: 12,
            fontWeight: 600,
            fill: '#374151'
          },
          labelBgStyle: {
            fill: '#ffffff',
            fillOpacity: 0.9
          }
        }
      : {})
  }));
}
